import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import type { SiteLearningWorkspace } from '@common/domain/learning-workspace';
import { AvatarTeachingRegion } from './AvatarTeachingRegion';
import { LearningMaterialReader } from './LearningMaterialReader';
import { resolveLearningRoute, type LearningRouteResolution } from './learningWorkspaceRoute';
import { parseModuleOrder, resolveModuleAssignments } from './moduleOrder';
import { ResizableLearningSplit } from './ResizableLearningSplit';
import { ChatTeachingRegion } from './ChatTeachingRegion';
import { TextbookTeachingRegion } from './TextbookTeachingRegion';
import { useLearningWorkspacePersona } from './useLearningWorkspacePersona';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { useStudyWorkspaceTelemetry } from './useStudyWorkspaceTelemetry';
import { useLearningConversationLease } from './useLearningConversationLease';
import './LearningWorkspace.css';
import { isAnyAdmin } from '@common/domain/auth';

export interface NonTextbookMaterialPanelConfig {
  title: string;
  imageAlt: string;
  imageSrc: string;
}

export interface LearningWorkspacePageProps {
  workspace: SiteLearningWorkspace;
  personaId?: string;
  nonTextbookMaterialPanel?: NonTextbookMaterialPanelConfig;
}

function UnavailableState({ children }: { children: string }) {
  return (
    <Box className="learning-workspace-page__state">
      <Typography component="h1" variant="h4">{children}</Typography>
    </Box>
  );
}

function ReadyLearningWorkspace({
  resolution,
  personaId,
  personaSlug,
  nonTextbookMaterialPanel,
}: {
  resolution: Extract<LearningRouteResolution, { kind: 'ready' }>;
  personaId?: string;
  personaSlug: string;
  nonTextbookMaterialPanel?: LearningWorkspacePageProps['nonTextbookMaterialPanel'];
}) {
  const { userInfo } = useUserInfoContext();
  const personaState = useLearningWorkspacePersona(personaId);
  const defaultMaterialSlug = useMemo(() => {
    const assignments = resolveModuleAssignments(
      resolution.materials,
      parseModuleOrder(userInfo?.moduleOrder),
    );
    const currentModalityAssignment = assignments.find((a) => a.modality === resolution.modality);
    return (
      (!resolution.scopedChapterSlug && currentModalityAssignment?.chapterSlug)
      || resolution.materials[0].slug
    );
  }, [resolution.materials, resolution.scopedChapterSlug, resolution.modality, userInfo?.moduleOrder]);
  const [selectedMaterialSlug, setSelectedMaterialSlug] = useState(defaultMaterialSlug);
  const [materialVisible, setMaterialVisible] = useState(true);
  const [materialPercent, setMaterialPercent] = useState(40);
  const [narrowSurface, setNarrowSurface] = useState<'material' | 'teaching'>('material');
  const [narrowMode, setNarrowMode] = useState(false);
  const materialHeadingRef = useRef<HTMLHeadingElement>(null);
  const teachingHeadingRef = useRef<HTMLHeadingElement>(null);
  const materialActionRef = useRef<HTMLButtonElement>(null);
  const pendingFocusRef = useRef<'material' | 'teaching' | 'action' | null>(null);
  const learnerPickedRef = useRef(false);
  const selectedMaterial = resolution.materials.find(
    (material) => material.slug === selectedMaterialSlug,
  ) ?? resolution.materials[0];
  const { modality, scopedChapterSlug } = resolution;
  const readyPersonaId = personaState.status === 'ready' ? personaState.persona.id : undefined;
  const conversationLeaseScope = useMemo(() => (
    userInfo?.userId
      && readyPersonaId
      && (modality === 'chat' || modality === 'avatar')
      ? {
          userId: userInfo.userId,
          personaId: readyPersonaId,
          modality,
          chapterSlug: selectedMaterial.slug,
        }
      : undefined
  ), [modality, readyPersonaId, selectedMaterial.slug, userInfo?.userId]);
  const {
    boundaryKey,
    conversationId,
    rememberConversation,
    startNewConversation,
  } = useLearningConversationLease(
    conversationLeaseScope,
  );
  const allowNewChat = isAnyAdmin(userInfo?.groups);
  const telemetry = useStudyWorkspaceTelemetry({
    accessToken: userInfo?.accessToken,
    ready: personaState.status === 'ready',
    personaId: personaState.status === 'ready' ? personaState.persona.id : undefined,
    personaSlug,
    modality,
    chapterSlug: selectedMaterial.slug,
    materialVisible,
    materialPercent,
    narrowMode,
    narrowSurface,
  });
  const linkChatRecord = useCallback(
    (recordId: string) => {
      rememberConversation(recordId);
      return telemetry.linkRecord(recordId, {
        personaId: personaState.status === 'ready' ? personaState.persona.id : '',
        modality: 'chat',
      });
    },
    [personaState, rememberConversation, telemetry.linkRecord],
  );
  const linkAvatarRecord = useCallback(
    (recordId: string) => {
      rememberConversation(recordId);
      return telemetry.linkRecord(recordId, {
        personaId: personaState.status === 'ready' ? personaState.persona.id : '',
        modality: 'avatar',
      });
    },
    [personaState, rememberConversation, telemetry.linkRecord],
  );

  // Auth can supply custom:module_order after this component mounts (returning
  // user / token refresh in auth.tsx). Adopt the claim-derived default whenever
  // it changes, until the learner chooses a chapter themselves.
  useEffect(() => {
    if (learnerPickedRef.current) return;
    setSelectedMaterialSlug(defaultMaterialSlug);
  }, [defaultMaterialSlug]);

  useEffect(() => {
    if (!resolution.materials.some(({ slug }) => slug === selectedMaterialSlug)) {
      setSelectedMaterialSlug(defaultMaterialSlug);
    }
  }, [resolution.materials, selectedMaterialSlug, defaultMaterialSlug]);

  const lastModalityRef = useRef(modality);
  useEffect(() => {
    if (lastModalityRef.current === modality) return;
    lastModalityRef.current = modality;
    learnerPickedRef.current = false;
    setSelectedMaterialSlug(defaultMaterialSlug);
  }, [modality, defaultMaterialSlug]);

  useEffect(() => {
    const pendingFocus = pendingFocusRef.current;
    if (!pendingFocus) return;

    pendingFocusRef.current = null;
    if (pendingFocus === 'material') materialHeadingRef.current?.focus();
    else if (pendingFocus === 'teaching') teachingHeadingRef.current?.focus();
    else materialActionRef.current?.focus();
  }, [materialVisible, narrowSurface]);

  if (personaState.status === 'loading') {
    return (
      <Box className="learning-workspace-page__state">
        <CircularProgress aria-label="Loading companion" />
      </Box>
    );
  }
  if (personaState.status === 'unavailable') {
    return <UnavailableState>This companion is unavailable for this account</UnavailableState>;
  }
  if (personaState.status === 'error') {
    return (
      <Box className="learning-workspace-page__state">
        <Typography component="h1" variant="h4">Unable to load this companion</Typography>
        <Button onClick={personaState.retry} variant="contained">Retry</Button>
      </Box>
    );
  }

  const showsCompanionPanel = modality !== 'textbook' && Boolean(nonTextbookMaterialPanel);
  const materialActionLabel = showsCompanionPanel
    ? materialVisible ? 'Hide chapter information' : 'Show chapter information'
    : scopedChapterSlug
    ? `${materialVisible ? 'Hide' : 'Open'} ${scopedChapterSlug}`
    : materialVisible ? 'Hide materials' : 'Learning Materials';

  const handleMaterialAction = () => {
    const nextVisible = !materialVisible;
    const nextNarrowSurface = narrowMode
      ? (nextVisible ? 'material' : 'teaching')
      : narrowSurface;
    telemetry.noteMaterialVisibility(nextVisible, {
      chapterSlug: selectedMaterial.slug,
      materialVisible: nextVisible,
      materialPercent,
      narrowMode,
      narrowSurface: nextNarrowSurface,
    });
    setMaterialVisible(nextVisible);

    if (nextVisible) {
      setNarrowSurface('material');
      if (narrowMode) pendingFocusRef.current = 'material';
    } else if (narrowMode) {
      pendingFocusRef.current = 'action';
      setNarrowSurface('teaching');
    }
  };

  const handleTextbookSurfaceToggle = () => {
    const nextSurface = narrowSurface === 'material' ? 'teaching' : 'material';
    pendingFocusRef.current = nextSurface;
    setNarrowSurface(nextSurface);
  };

  const materialRegion = (
    <Box
      className={`learning-workspace-page__material-region${showsCompanionPanel ? ' learning-workspace-page__material-region--companion' : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: showsCompanionPanel ? undefined : 'hidden',
      }}
    >
      {showsCompanionPanel ? (
        <Box
          aria-labelledby="learning-workspace-companion-title"
          className="learning-workspace-page__companion-panel"
          component="section"
          data-testid="non-textbook-companion-panel"
        >
          <img
            alt={nonTextbookMaterialPanel?.imageAlt}
            className="learning-workspace-page__companion-portrait"
            src={nonTextbookMaterialPanel?.imageSrc}
          />
          <Typography
            className="learning-workspace-page__companion-title"
            component="h1"
            id="learning-workspace-companion-title"
            ref={materialHeadingRef}
            tabIndex={-1}
            variant="h5"
          >
            {nonTextbookMaterialPanel?.title}
          </Typography>
          <Typography className="learning-workspace-page__companion-chapter">
            {selectedMaterial.companionDescription ?? selectedMaterial.title}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography component="h1" ref={materialHeadingRef} tabIndex={-1} variant="h5">
            Learning material: {selectedMaterial.title}
          </Typography>
          <LearningMaterialReader
            fillAvailableHeight
            material={selectedMaterial}
            onInteraction={telemetry.noteMaterialInteraction}
            onProgressMilestone={telemetry.noteMaterialProgress}
          />
        </>
      )}
    </Box>
  );
  const teachingRegion = modality === 'textbook' ? (
      <TextbookTeachingRegion
        ref={teachingHeadingRef}
        persona={personaState.persona}
        material={selectedMaterial}
        onTeachingInteraction={telemetry.noteTeachingInteraction}
      />
    ) : modality === 'chat' ? (
      <ChatTeachingRegion
        persona={personaState.persona}
        material={selectedMaterial}
        conversationBoundaryKey={boundaryKey}
        conversationSessionId={conversationId}
        studySessionId={telemetry.studySessionId}
        allowNewChat={allowNewChat}
        onNewChat={startNewConversation}
        onTeachingInteraction={telemetry.noteTeachingInteraction}
        onChatSessionIdentified={linkChatRecord}
      />
    ) : (
      <AvatarTeachingRegion
        ref={teachingHeadingRef}
        persona={personaState.persona}
        material={selectedMaterial}
        conversationBoundaryKey={boundaryKey}
        conversationSessionId={conversationId}
        studySessionId={telemetry.studySessionId}
        allowNewChat={allowNewChat}
        onNewChat={startNewConversation}
        onTeachingInteraction={telemetry.noteTeachingInteraction}
        onConversationIdentified={linkAvatarRecord}
        onTranscriptVisibilityChange={telemetry.noteTranscriptVisibility}
      />
    );

  return (
    <Box className="learning-workspace-page" data-testid="learning-workspace">
      <Box className="learning-workspace-page__toolbar" data-testid="learning-workspace-toolbar">
        <span data-testid="active-material-slug" hidden>{selectedMaterial.slug}</span>
        <span data-modality={modality} data-testid="teaching-modality" hidden>{modality}</span>
        {!scopedChapterSlug && (
          <FormControl size="small">
            <InputLabel htmlFor="learning-material-picker">Learning Materials</InputLabel>
            <Select
              native
              id="learning-material-picker"
              label="Learning Materials"
              inputProps={{ 'aria-label': 'Learning Materials' }}
              value={selectedMaterial.slug}
              onChange={(event) => {
                const chapterSlug = event.target.value as string;
                learnerPickedRef.current = true;
                telemetry.noteMaterialSelection(chapterSlug);
                setSelectedMaterialSlug(chapterSlug);
              }}
            >
              {resolution.materials.map((material) => (
                <option key={material.slug} value={material.slug}>{material.title}</option>
              ))}
            </Select>
          </FormControl>
        )}
        {modality !== 'textbook' && (
          <Button
            ref={materialActionRef}
            aria-expanded={materialVisible}
            onClick={handleMaterialAction}
            startIcon={<MenuBookRoundedIcon />}
            variant="outlined"
          >
            {materialActionLabel}
          </Button>
        )}
        {modality === 'textbook' && narrowMode && (
          <Button
            onClick={handleTextbookSurfaceToggle}
            startIcon={<MenuBookRoundedIcon />}
            variant="outlined"
          >
            {narrowSurface === 'material' ? 'Curriculum guide' : 'Learning Materials'}
          </Button>
        )}
      </Box>
      <ResizableLearningSplit
        activeNarrowSurface={narrowSurface}
        initialMaterialPercent={materialPercent}
        material={materialRegion}
        materialVisible={materialVisible}
        onLayoutChange={(layout) => {
          const nextNarrowSurface = layout.activeSurface === 'material'
            ? 'material'
            : layout.activeSurface === 'teaching' ? 'teaching' : narrowSurface;
          const nextMaterialPercent = layout.activeSurface === 'split'
            ? layout.materialPercent
            : materialPercent;
          telemetry.syncLayout({
            chapterSlug: selectedMaterial.slug,
            materialVisible,
            materialPercent: nextMaterialPercent,
            narrowMode: layout.narrowMode,
            narrowSurface: nextNarrowSurface,
          });
          if (layout.activeSurface === 'split') setMaterialPercent(layout.materialPercent);
        }}
        onNarrowModeChange={setNarrowMode}
        onResizeEnd={(nextMaterialPercent, direction) => {
          telemetry.noteDividerResize(nextMaterialPercent, direction);
          setMaterialPercent(nextMaterialPercent);
        }}
        teaching={teachingRegion}
      />
    </Box>
  );
}

export function LearningWorkspacePage({
  workspace,
  personaId,
  nonTextbookMaterialPanel,
}: LearningWorkspacePageProps) {
  const params = useParams<{
    personaSlug: string;
    modality: string;
    chapterSlug?: string;
  }>();
  const resolution = resolveLearningRoute(workspace, params);

  if (resolution.kind === 'unavailable') {
    const message = resolution.reason === 'chapter_unavailable'
      ? 'This learning material is unavailable'
      : 'Learning workspace is unavailable';
    return <UnavailableState>{message}</UnavailableState>;
  }

  return (
    <ReadyLearningWorkspace
      resolution={resolution}
      personaId={personaId}
      personaSlug={workspace.persona.slug}
      nonTextbookMaterialPanel={nonTextbookMaterialPanel}
    />
  );
}
